import React, { useEffect, useMemo, useState } from 'react'
import { useSyncedState } from '../../hooks/useSyncedState'
import { api, formatCurrency } from '../../utils/api'
import { useAuth } from '../../hooks/useAuth'
import { useToast } from '../../hooks/useToast'

// ---------------------------------------------------------------------------
// Pricing Rules — READ-ONLY view of the live engine curve + rollout control
// plane. The curve and its rounded reference prices come from the backend
// (GET /api/pricing/curve) so this never re-implements the pricing math; the
// rollout status comes from GET /api/pricing/config. Editing the rollout or the
// curve is a separate, security-reviewed phase (spec §6.11) — not wired here.
//
// Model (2026-06-18): D2C (Shopify) follows an explicit 12-month anchor
// multiplier table; eBay and Reverb are a per-month % uplift on the rounded
// D2C price (no curve of their own). M-by-M tuning lives in the backend tables.
// ---------------------------------------------------------------------------

const CHANNELS = [
  { key: 'shopify', label: 'D2C (Shopify)', color: '#96BF48' },
  { key: 'reverb',  label: 'Reverb',        color: '#E4002B' },
  { key: 'ebay',    label: 'eBay',          color: '#0064D2' },
]

const MASTER_CHANNEL = '*'

// Reverb autobump bid ladder: 0.5%-7.0% in 0.5 steps => 50-700 bps in steps of
// 50 (mirrors the CHECK constraint on pricing_config.bump_bid_bps).
const BUMP_BID_MIN_BPS = 50
const BUMP_BID_MAX_BPS = 700
const BUMP_BID_STEP_BPS = 50
const BUMP_BID_OPTIONS = []
for (let bps = BUMP_BID_MIN_BPS; bps <= BUMP_BID_MAX_BPS; bps += BUMP_BID_STEP_BPS) {
  BUMP_BID_OPTIONS.push(bps)
}
const BUMP_CHANNEL = 'reverb'

function pct(mult) {
  // 1.05 → "+5%", 0.875 → "−12.5%", 1.0 → "0%"
  const p = (mult - 1) * 100
  const rounded = Math.round(p * 100) / 100
  if (rounded === 0) return '0%'
  return `${rounded > 0 ? '+' : '−'}${Math.abs(rounded)}%`
}

// ---------------------------------------------------------------------------
// Multi-series preview chart (inline SVG, no deps). Each series is the channel's
// effective anchor multiplier per month (rounded reference price ÷ anchor).
// ---------------------------------------------------------------------------

function CurveChart({ series, months, height = 220 }) {
  const W = 640, H = height
  const PAD_L = 44, PAD_R = 12, PAD_T = 12, PAD_B = 26
  const plotW = W - PAD_L - PAD_R
  const plotH = H - PAD_T - PAD_B
  const xMin = 1, xMax = months[months.length - 1]
  const allMults = series.flatMap(s => s.points)
  const yMin = Math.min(0.65, ...allMults) - 0.03
  const yMax = Math.max(1.15, ...allMults) + 0.03
  const xS = m => PAD_L + ((m - xMin) / (xMax - xMin)) * plotW
  const yS = v => PAD_T + ((yMax - v) / (yMax - yMin)) * plotH
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={{ width: '100%', height: 'auto', display: 'block' }}>
      {[0.7, 0.85, 1.0, 1.05].map(g => (
        <g key={g}>
          <line x1={PAD_L} y1={yS(g)} x2={W - PAD_R} y2={yS(g)} stroke="#374151" strokeDasharray="2,2" strokeOpacity="0.4" />
          <text x={PAD_L - 6} y={yS(g) + 3} textAnchor="end" fontSize="9" fill="#6b7280">{`${Math.round(g * 100)}%`}</text>
        </g>
      ))}
      {months.filter(m => m === 1 || m % 3 === 0).map(m => (
        <g key={m}>
          <line x1={xS(m)} y1={PAD_T} x2={xS(m)} y2={H - PAD_B} stroke="#374151" strokeOpacity="0.2" />
          <text x={xS(m)} y={H - PAD_B + 14} textAnchor="middle" fontSize="9" fill="#6b7280">{m === xMax ? `M${m}+` : `M${m}`}</text>
        </g>
      ))}
      <line x1={PAD_L} y1={yS(1.0)} x2={W - PAD_R} y2={yS(1.0)} stroke="#6b7280" strokeOpacity="0.5" />
      {series.map(s => (
        <g key={s.key}>
          <polyline fill="none" stroke={s.color} strokeWidth="1.75" points={months.map((m, i) => `${xS(m)},${yS(s.points[i])}`).join(' ')} />
          {months.map((m, i) => <circle key={m} cx={xS(m)} cy={yS(s.points[i])} r="2" fill={s.color} />)}
        </g>
      ))}
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Reverb autobump editor — the two bump knobs (bid % and stock-age gate).
// pricing:write only; PATCH /api/pricing/config (channel='reverb'). No actor
// field — the worker injects it from the session (SEC1).
// ---------------------------------------------------------------------------

const REVERB = CHANNELS.find(c => c.key === BUMP_CHANNEL)

function bumpSummary(row) {
  if (row?.bump_bid_bps == null || row?.bump_after_days == null) return 'Not configured yet'
  const days = row.bump_after_days
  return `+${(row.bump_bid_bps / 100).toFixed(1)}% after ${days} ${days === 1 ? 'day' : 'days'} unsold`
}

function AutobumpEditor({ row, canWrite, onSaved }) {
  const { toast } = useToast()
  const [bidBps, setBidBps] = useSyncedState(row?.bump_bid_bps ?? BUMP_BID_OPTIONS[0])
  const [afterDays, setAfterDays] = useSyncedState(row?.bump_after_days ?? 0)
  const [saving, setSaving] = useState(false)
  // Sticks after a successful save (until the next edit) so the greyed-out
  // Save button reads as "nothing left to save", not as a dead control.
  const [justSaved, setJustSaved] = useState(false)

  const dirty = row != null && (bidBps !== row.bump_bid_bps || afterDays !== row.bump_after_days)

  function handleReset() {
    setBidBps(row?.bump_bid_bps ?? BUMP_BID_OPTIONS[0])
    setAfterDays(row?.bump_after_days ?? 0)
  }

  async function handleSave() {
    if (!dirty || saving) return
    setSaving(true)
    try {
      const payload = { channel: BUMP_CHANNEL }
      if (bidBps !== row.bump_bid_bps) payload.bump_bid_bps = bidBps
      if (afterDays !== row.bump_after_days) payload.bump_after_days = afterDays
      const updated = await api.patch('/api/pricing/config', payload)
      onSaved?.(updated)
      setJustSaved(true)
      toast.success('Autobump settings saved')
    } catch (e) {
      toast.error(e.message || 'Failed to save autobump settings')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card" style={{ padding: '12px 16px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 2 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>
          <span style={{ color: REVERB.color }}>●</span> Reverb autobump
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: row?.bump_bid_bps != null ? '#6b7280' : '#f59e0b' }}>
          {bumpSummary(row)}
        </span>
      </div>
      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 12 }}>
        Periodically re-bids Reverb listings to keep them near the top of search once they've sat
        unsold past the age gate.
      </div>

      {!row ? (
        <div style={{ fontSize: 12, color: '#6b7280' }}>No reverb config row found.</div>
      ) : !canWrite ? (
        <div style={{ fontSize: 11, color: '#9ca3af' }}>
          Read-only — requires the pricing:write permission to edit.
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="autobump-bid-bps">Bump %</label>
            <select
              id="autobump-bid-bps"
              className="form-input"
              value={bidBps}
              disabled={saving}
              onChange={e => { setJustSaved(false); setBidBps(Number(e.target.value)) }}
              style={{ width: 110 }}
            >
              {BUMP_BID_OPTIONS.map(bps => (
                <option key={bps} value={bps}>{(bps / 100).toFixed(1)}%</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="autobump-after-days">Bump after (days)</label>
            <input
              id="autobump-after-days"
              className="form-input"
              type="number"
              min={0}
              step={1}
              value={afterDays}
              disabled={saving}
              onChange={e => { setJustSaved(false); setAfterDays(Math.max(0, Math.trunc(Number(e.target.value) || 0))) }}
              style={{ width: 110 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingBottom: 1 }}>
            {dirty && (
              <button type="button" className="btn btn-sm btn-secondary" disabled={saving} onClick={handleReset}>
                Reset
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={!dirty || saving}
              onClick={handleSave}
              style={(!dirty || saving) ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            {dirty && !saving && (
              <span style={{ fontSize: 11, color: '#d97706', fontWeight: 600 }}>unsaved changes</span>
            )}
            {justSaved && !dirty && !saving && (
              <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>✓ Saved</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// D2C floor editor — the flat minimum markup over buy price (grill 2026-07-14):
// floor = cost × (1 + m). Stored in bps on the shopify row; edited as a %.
// pricing:write only; enforced by the nightly apply job's floor clamp.
// ---------------------------------------------------------------------------

const FLOOR_MIN_PCT = 0
const FLOOR_MAX_PCT = 50 // mirrors ck_pricing_config_floor_min_margin_bps (0-5000 bps)

function FloorEditor({ row, canWrite, onSaved }) {
  const { toast } = useToast()
  const [pctText, setPctText] = useSyncedState(row ? String(row.floor_min_margin_bps / 100) : '')
  const [saving, setSaving] = useState(false)
  const [justSaved, setJustSaved] = useState(false)

  const parsed = Number(pctText)
  const valid = pctText !== '' && Number.isFinite(parsed)
    && parsed >= FLOOR_MIN_PCT && parsed <= FLOOR_MAX_PCT
  const targetBps = valid ? Math.round(parsed * 100) : null
  const dirty = row != null && targetBps != null && targetBps !== row.floor_min_margin_bps

  async function handleSave() {
    if (!dirty || saving) return
    setSaving(true)
    try {
      const updated = await api.patch('/api/pricing/config', {
        channel: 'shopify',
        floor_min_margin_bps: targetBps,
      })
      onSaved?.(updated)
      setJustSaved(true)
      toast.success('Price floor saved')
    } catch (e) {
      toast.error(e.message || 'Failed to save the price floor')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card" style={{ padding: '12px 16px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 2 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>D2C price floor</div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>
          {row ? `floor = buy price + ${(row.floor_min_margin_bps / 100)}%` : 'Not configured'}
        </span>
      </div>
      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 12 }}>
        The engine never pushes a price below <em>buy price × (1 + floor%)</em> — flat, fees not
        included (the Management page shows fee-aware margins). Floor-parked items hold there until
        sold or overridden. eBay &amp; Reverb inherit it by riding the floored D2C.
      </div>
      {!row ? (
        <div style={{ fontSize: 12, color: '#6b7280' }}>No shopify config row found.</div>
      ) : !canWrite ? (
        <div style={{ fontSize: 11, color: '#9ca3af' }}>
          Read-only — requires the pricing:write permission to edit.
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="floor-min-margin-pct">Minimum markup %</label>
            <input
              id="floor-min-margin-pct"
              className="form-input"
              type="number"
              min={FLOOR_MIN_PCT}
              max={FLOOR_MAX_PCT}
              step={0.5}
              value={pctText}
              disabled={saving}
              onChange={e => { setJustSaved(false); setPctText(e.target.value) }}
              style={{ width: 110 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingBottom: 1 }}>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              aria-label="Save price floor"
              disabled={!dirty || saving}
              onClick={handleSave}
              style={(!dirty || saving) ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            {pctText !== '' && !valid && (
              <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>
                {FLOOR_MIN_PCT}–{FLOOR_MAX_PCT}% only
              </span>
            )}
            {dirty && !saving && valid && (
              <span style={{ fontSize: 11, color: '#d97706', fontWeight: 600 }}>unsaved changes</span>
            )}
            {justSaved && !dirty && !saving && (
              <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>✓ Saved</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Consignment terms editor (spec D15 + D16) — the two '*'-row settlement knobs:
//  · floor % — the % of the agreed listing price a consignment item is never
//    priced below without contacting the seller (D15);
//  · default seller share % — the split stamped onto NEW offers that carry no
//    negotiated split (D16: the split IS the seller's share, rendered raw).
// Both are frozen onto the quote/item at offer/stamp time, so edits here only
// affect offers sent and items listed AFTER the change. The engine skips
// consignment entirely (skip reason `consignment`) — the floor guards MANUAL
// edits, which warn-and-confirm on every inventory price-write path.
// ---------------------------------------------------------------------------

const CONSIGNMENT_PCT_MIN = 0
const CONSIGNMENT_PCT_MAX = 100 // mirrors both pricing_config CHECK constraints

function ConsignmentTermsEditor({ row, canWrite, onSaved }) {
  const { toast } = useToast()
  const [floorText, setFloorText] = useSyncedState(row?.consignment_floor_pct != null ? String(row.consignment_floor_pct) : '')
  const [splitText, setSplitText] = useSyncedState(row?.consignment_default_split_pct != null ? String(row.consignment_default_split_pct) : '')
  const [saving, setSaving] = useState(false)
  const [justSaved, setJustSaved] = useState(false)

  const wholePct = (text) => {
    const n = Number(text)
    return text !== '' && Number.isInteger(n) && n >= CONSIGNMENT_PCT_MIN && n <= CONSIGNMENT_PCT_MAX ? n : null
  }
  const floorParsed = wholePct(floorText)
  const splitParsed = wholePct(splitText)
  const floorInvalid = floorText !== '' && floorParsed == null
  const splitInvalid = splitText !== '' && splitParsed == null
  const floorDirty = row != null && floorParsed != null && floorParsed !== row.consignment_floor_pct
  const splitDirty = row != null && splitParsed != null && splitParsed !== row.consignment_default_split_pct
  const dirty = floorDirty || splitDirty

  async function handleSave() {
    if (!dirty || saving) return
    setSaving(true)
    try {
      const payload = { channel: MASTER_CHANNEL }
      if (floorDirty) payload.consignment_floor_pct = floorParsed
      if (splitDirty) payload.consignment_default_split_pct = splitParsed
      const updated = await api.patch('/api/pricing/config', payload)
      onSaved?.(updated)
      setJustSaved(true)
      toast.success('Consignment terms saved')
    } catch (e) {
      toast.error(e.message || 'Failed to save the consignment terms')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card" style={{ padding: '12px 16px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 2 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>Consignment terms</div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>
          {row?.consignment_floor_pct != null
            ? `never below ${row.consignment_floor_pct}% · seller receives ${row.consignment_default_split_pct ?? 70}% by default`
            : 'Not configured'}
        </span>
      </div>
      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 12 }}>
        The Commission Service terms for NEW offers: the seller share stamped onto an offer that
        carries no negotiated split (the customer receives this % of the sale), and the floor —
        we never price a seller's item below this % of their agreed listing price without
        contacting them first. Both are frozen onto the quote at offer time and per item at
        listing time, so changes here never move an existing seller's terms. Consignment items
        are excluded from the engine; manual edits below a frozen floor warn and require
        confirmation.
      </div>
      {!row ? (
        <div style={{ fontSize: 12, color: '#6b7280' }}>No master config row found.</div>
      ) : !canWrite ? (
        <div style={{ fontSize: 11, color: '#9ca3af' }}>
          Read-only — requires the pricing:write permission to edit.
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="consignment-floor-pct">Floor %</label>
            <input
              id="consignment-floor-pct"
              className="form-input"
              type="number"
              min={CONSIGNMENT_PCT_MIN}
              max={CONSIGNMENT_PCT_MAX}
              step={1}
              value={floorText}
              disabled={saving}
              onChange={e => { setJustSaved(false); setFloorText(e.target.value) }}
              style={{ width: 110 }}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="consignment-default-split-pct">Default seller share %</label>
            <input
              id="consignment-default-split-pct"
              className="form-input"
              type="number"
              min={CONSIGNMENT_PCT_MIN}
              max={CONSIGNMENT_PCT_MAX}
              step={1}
              value={splitText}
              disabled={saving}
              onChange={e => { setJustSaved(false); setSplitText(e.target.value) }}
              style={{ width: 150 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingBottom: 1 }}>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              aria-label="Save consignment terms"
              disabled={!dirty || saving}
              onClick={handleSave}
              style={(!dirty || saving) ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            {(floorInvalid || splitInvalid) && (
              <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>
                whole {CONSIGNMENT_PCT_MIN}–{CONSIGNMENT_PCT_MAX}% only
              </span>
            )}
            {dirty && !saving && (
              <span style={{ fontSize: 11, color: '#d97706', fontWeight: 600 }}>unsaved changes</span>
            )}
            {justSaved && !dirty && !saving && (
              <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>✓ Saved</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Consignment curve — rollout controls (enable / dry-run) for the descent-to-
// floor curve, plus a read-only preview of the descent. The curve shape is code
// (ADR-0024); only the rollout is editable here. The flags live on the '*'
// master row and are SEPARATE from the D2C rollout. Start-asks / stop-instant
// confirm asymmetry, same as the D2C toggles (grill 2026-07-14).
// ---------------------------------------------------------------------------

const CONSIGNMENT_CURVE_COLOR = '#9333ea'

function ConsignmentCurveCard({ row, curve, canWrite, onSaved }) {
  const { toast } = useToast()
  const [saving, setSaving] = useState('')

  async function saveFlag(field, value, confirmMsg) {
    if (confirmMsg && !window.confirm(confirmMsg)) return
    setSaving(field)
    try {
      const updated = await api.patch('/api/pricing/config', { channel: MASTER_CHANNEL, [field]: value })
      onSaved?.(updated)
      toast.success('Consignment curve saved')
    } catch (e) {
      toast.error(e.message || 'Failed to save the consignment curve rollout')
    } finally {
      setSaving('')
    }
  }

  function toggleEnabled() {
    const enabling = !row.consignment_curve_enabled
    const confirmMsg = enabling
      ? `Enable the consignment curve? ${row.consignment_curve_dry_run
        ? '(Still dry-run — no pushes until Mode is LIVE.)'
        : 'It is LIVE — real consignment prices will be pushed.'}`
      : null // disable = kill switch, instant
    saveFlag('consignment_curve_enabled', enabling, confirmMsg)
  }

  function toggleMode() {
    const goingLive = row.consignment_curve_dry_run
    const confirmMsg = goingLive
      ? `Set the consignment curve to LIVE? The engine will start pushing real consignment price changes${row.consignment_curve_enabled ? '' : ' once it is enabled'}.`
      : null // back to dry-run = stopping, instant
    saveFlag('consignment_curve_dry_run', !goingLive, confirmMsg)
  }

  const floorPct = curve?.consignment_reference_floor_pct
  const monthsToFloor = curve?.consignment_months_to_floor
  // Per channel, like the D2C reference_prices: { channel: { month: cents } }.
  const refPrices = curve?.consignment_reference_prices
  const d2cDescent = refPrices?.shopify
  const months = d2cDescent ? Object.keys(d2cDescent).map(Number).sort((a, b) => a - b) : []
  const startCents = months.length ? d2cDescent[months[0]] : null
  const floorCents = months.length ? d2cDescent[months[months.length - 1]] : null
  // One descent line (the D2C glide). All channels share the same % shape — they
  // only differ by a flat uplift — so plotting three normalised lines would just
  // overlap. The per-channel £ values live in the table below instead. Normalise
  // against the month-1 list price, not the raw anchor: round-up can nudge month 1
  // a hair above the anchor, which would plot the curve starting above 100%.
  const descentSeries = startCents
    ? [{ key: 'consignment', color: CONSIGNMENT_CURVE_COLOR, label: 'Consignment', points: months.map(m => d2cDescent[m] / startCents) }]
    : []

  const enabled = !!row?.consignment_curve_enabled
  const dryRun = !!row?.consignment_curve_dry_run
  const statusText = !enabled ? 'OFF' : dryRun ? 'DRY-RUN' : 'LIVE'
  const statusColor = !enabled ? '#6b7280' : dryRun ? '#f59e0b' : '#22c55e'

  return (
    <div className="card" style={{ padding: '12px 16px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 2 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>Consignment curve</div>
        <span className="badge badge-purple">Consignment</span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: statusColor }}>
          {statusText}{floorPct != null ? ` · to ${floorPct}% floor over ${monthsToFloor}mo` : ''}
        </span>
      </div>
      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 12 }}>
        Consignment stock descends linearly from its agreed listing price to that item's promised
        floor over {monthsToFloor ?? 9} months, then holds — never below the floor. The shape is code
        (ADR-0024); only the rollout is editable here, with its own switches, separate from the D2C
        rollout and under the same master kill switch. Going below the floor is a manual, off-system
        step (contact the seller first).
      </div>

      {!row ? (
        <div style={{ fontSize: 12, color: '#6b7280' }}>No master config row found.</div>
      ) : (
        <table className="table" style={{ fontSize: 12, marginBottom: descentSeries.length ? 12 : 0 }}>
          <thead>
            <tr><th style={{ width: 110 }}>Enabled</th><th style={{ width: 130 }}>Mode</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>
                {canWrite ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    disabled={saving !== ''}
                    onClick={toggleEnabled}
                    style={{ minWidth: 64, fontWeight: 600, color: enabled ? '#16a34a' : '#ef4444' }}
                    title={enabled ? 'Disable the consignment curve (instant)' : 'Enable the consignment curve'}
                  >
                    {saving === 'consignment_curve_enabled' ? '…' : enabled ? 'Yes' : 'No'}
                  </button>
                ) : (
                  <span style={{ fontWeight: 600, color: enabled ? '#16a34a' : '#ef4444' }}>{enabled ? 'Yes' : 'No'}</span>
                )}
              </td>
              <td>
                {canWrite ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    disabled={saving !== ''}
                    onClick={toggleMode}
                    style={{ minWidth: 84, fontWeight: 600, color: dryRun ? '#f59e0b' : '#22c55e' }}
                    title={dryRun ? 'Go LIVE — start pushing real consignment prices' : 'Back to dry-run (instant)'}
                  >
                    {saving === 'consignment_curve_dry_run' ? '…' : dryRun ? 'DRY-RUN' : 'LIVE'}
                  </button>
                ) : (
                  <span style={{ fontWeight: 600, color: dryRun ? '#f59e0b' : '#22c55e' }}>{dryRun ? 'DRY-RUN' : 'LIVE'}</span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {descentSeries.length > 0 && (
        <>
          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>
            Descent preview for a sample item at the {floorPct}% floor:{' '}
            lists at {formatCurrency(startCents / 100)} now, reaches {formatCurrency(floorCents / 100)} by month {monthsToFloor}, then holds.
          </div>
          <CurveChart series={descentSeries} months={months} height={160} />
          <div style={{ overflowX: 'auto', marginTop: 12 }}>
            <table className="table" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Month</th>
                  <th style={{ width: 100 }}>% of agreed</th>
                  <th style={{ width: 110 }}><span style={{ color: CHANNELS[0].color }}>● </span>D2C</th>
                  <th style={{ width: 110 }}><span style={{ color: CHANNELS[1].color }}>● </span>Reverb</th>
                  <th style={{ width: 110 }}><span style={{ color: CHANNELS[2].color }}>● </span>eBay</th>
                </tr>
              </thead>
              <tbody>
                {months.map(m => (
                  <tr key={m}>
                    <td style={{ fontWeight: 600 }}>{m === months[months.length - 1] ? `M${m}+` : `M${m}`}</td>
                    <td>{Math.round((d2cDescent[m] / startCents) * 100)}%</td>
                    <td>{formatCurrency(refPrices.shopify[m] / 100)}</td>
                    <td>{formatCurrency(refPrices.reverb[m] / 100)}</td>
                    <td>{formatCurrency(refPrices.ebay[m] / 100)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function PricingRules() {
  const { hasPermission } = useAuth()
  const canWrite = hasPermission('pricing:write')
  const { toast } = useToast()
  const [curve, setCurve] = useState(null)
  const [config, setConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // channel currently saving a rollout toggle ('' = none) — disables that row's controls.
  const [rolloutSaving, setRolloutSaving] = useState('')

  useEffect(() => {
    let alive = true
    Promise.all([api.get('/api/pricing/curve'), api.get('/api/pricing/config')])
      .then(([c, cfg]) => { if (alive) { setCurve(c); setConfig(cfg) } })
      .catch(e => { if (alive) setError(e.message || 'Failed to load pricing rules') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  const configByChannel = useMemo(() => {
    const map = {}
    for (const r of config || []) map[r.channel] = r
    return map
  }, [config])

  const master = configByChannel[MASTER_CHANNEL]

  // Merge the PATCH response into the local config list so the editors and the
  // Rollout table both reflect the save without a full reload.
  function handleConfigSaved(updatedRow) {
    setConfig(prev => (prev || []).map(r => (r.channel === updatedRow.channel ? updatedRow : r)))
  }

  // Rollout toggles save immediately. Safety asymmetry (grill 2026-07-14):
  // anything that can START real pushes (enable, go LIVE) asks first; anything
  // that stops them (disable, back to dry-run) is instant — kill switches must
  // never sit behind a dialog.
  async function saveRolloutToggle(channel, field, value, confirmMsg) {
    if (confirmMsg && !window.confirm(confirmMsg)) return
    setRolloutSaving(channel)
    try {
      const updated = await api.patch('/api/pricing/config', { channel, [field]: value })
      handleConfigSaved(updated)
      toast.success(`${channel === MASTER_CHANNEL ? 'Master switch' : channel} saved`)
    } catch (e) {
      toast.error(e.message || 'Failed to save the rollout change')
    } finally {
      setRolloutSaving('')
    }
  }

  function toggleEnabled(row) {
    const enabling = !row.enabled
    const name = row.channel === MASTER_CHANNEL ? 'the MASTER switch' : row.channel
    const confirmMsg = enabling
      ? `Enable ${name}? ${row.channel === MASTER_CHANNEL
        ? 'Channels that are enabled and LIVE will start pushing real prices again.'
        : row.dry_run ? '(Still dry-run — no pushes until Mode is LIVE.)' : 'This channel is LIVE — real prices will be pushed.'}`
      : null // disabling = kill switch, instant
    saveRolloutToggle(row.channel, 'enabled', enabling, confirmMsg)
  }

  function toggleMode(row) {
    const goingLive = row.dry_run
    const confirmMsg = goingLive
      ? `Set ${row.channel} to LIVE? The engine will start pushing real price changes${row.enabled ? '' : ' once the channel is enabled'}.`
      : null // back to dry-run = stopping, instant
    saveRolloutToggle(row.channel, 'dry_run', !goingLive, confirmMsg)
  }

  const series = useMemo(() => {
    if (!curve) return []
    const anchor = curve.reference_anchor_cents
    return CHANNELS.map(c => ({
      key: c.key,
      color: c.color,
      label: c.label,
      points: curve.months.map(m => (curve.reference_prices[c.key][m] / anchor)),
    }))
  }, [curve])

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading…</div>
  if (error) return <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>{error}</div>
  if (!curve) return null

  const anchorPounds = formatCurrency(curve.reference_anchor_cents / 100)

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>Pricing Rules</h2>
        <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 13 }}>
          The live time-decay curve and rollout control plane — read-only. D2C follows a fixed 12-month
          anchor curve; eBay and Reverb are a per-month % uplift on the D2C price. Prices are recomputed on
          demand — there is no stored trajectory.
        </p>
      </div>

      {/* Editability banner */}
      <div className="card" style={{ padding: '10px 16px', marginBottom: 16, background: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
        <div style={{ fontSize: 12, color: '#3b82f6' }}>
          <strong>The curve is code.</strong> The 12-month decay table is engineering-reviewed business policy and
          is not editable here (ADR-0024). The rollout toggles, the D2C price floor and the Reverb autobump knobs
          are operational knobs — editable with pricing:write, every change audited.
        </div>
      </div>

      {/* Rollout control plane — toggles editable with pricing:write */}
      <div className="card" style={{ padding: '12px 16px', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Rollout</div>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: master?.enabled ? '#22c55e' : '#ef4444' }}>
            Master switch {master ? (master.enabled ? 'ON' : 'OFF (all repricing halted)') : '— (not configured)'}
          </span>
          {canWrite && master && (
            <button
              type="button"
              className={`btn btn-sm ${master.enabled ? 'btn-secondary' : 'btn-primary'}`}
              disabled={rolloutSaving !== ''}
              onClick={() => toggleEnabled(master)}
              title={master.enabled ? 'Halt ALL engine repricing immediately' : 'Re-enable the engine'}
            >
              {master.enabled ? 'Halt all' : 'Enable'}
            </button>
          )}
        </div>
        <table className="table" style={{ fontSize: 12 }}>
          <thead>
            <tr><th>Channel</th><th style={{ width: 110 }}>Enabled</th><th style={{ width: 130 }}>Mode</th><th style={{ width: 150 }}>Min age (months)</th></tr>
          </thead>
          <tbody>
            {CHANNELS.map(c => {
              const r = configByChannel[c.key]
              const saving = rolloutSaving === c.key
              // The engine pushes D2C only — eBay/Reverb prices FOLLOW D2C via
              // the channel fan-out, which no toggle here gates. Offering
              // enabled/dry-run controls on those rows would be fake controls
              // ("disable eBay" would NOT stop eBay repricing), so only the
              // shopify row is editable.
              const isD2C = c.key === 'shopify'
              return (
                <tr key={c.key}>
                  <td><span style={{ color: c.color }}>●</span> {c.label}</td>
                  <td>
                    {!r ? '—' : (isD2C && canWrite) ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        disabled={saving || rolloutSaving !== ''}
                        onClick={() => toggleEnabled(r)}
                        style={{ minWidth: 64, fontWeight: 600, color: r.enabled ? '#16a34a' : '#ef4444' }}
                        title={r.enabled ? 'Disable this channel (instant)' : 'Enable this channel'}
                      >
                        {saving ? '…' : r.enabled ? 'Yes' : 'No'}
                      </button>
                    ) : isD2C ? (r.enabled ? 'Yes' : 'No') : '—'}
                  </td>
                  <td>
                    {!r ? '—' : !isD2C ? (
                      <span
                        style={{ fontSize: 11, color: '#3b82f6', fontWeight: 600 }}
                        title="Repriced by the channel fan-out whenever the D2C price changes — no independent engine pushes, no toggle."
                      >
                        FOLLOWS D2C
                      </span>
                    ) : canWrite ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        disabled={saving || rolloutSaving !== ''}
                        onClick={() => toggleMode(r)}
                        style={{ minWidth: 84, fontWeight: 600, color: r.dry_run ? '#f59e0b' : '#22c55e' }}
                        title={r.dry_run ? 'Go LIVE — the engine starts pushing real prices' : 'Back to dry-run (instant)'}
                      >
                        {saving ? '…' : r.dry_run ? 'DRY-RUN' : 'LIVE'}
                      </button>
                    ) : (
                      <span style={{ fontSize: 11, color: r.dry_run ? '#f59e0b' : '#22c55e', fontWeight: 600 }}>
                        {r.dry_run ? 'DRY-RUN' : 'LIVE'}
                      </span>
                    )}
                  </td>
                  <td>{r ? r.min_age_months : '—'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
          The engine pushes D2C prices only; in dry-run it computes &amp; previews but pushes nothing.
          eBay &amp; Reverb have no independent engine pushes — they are repriced by the channel fan-out
          whenever the D2C price changes (stopping that is a channel-sync matter, not a toggle here).
        </div>
      </div>

      {/* D2C price floor — flat minimum markup over buy price, enforced by the apply job. */}
      <FloorEditor row={configByChannel.shopify} canWrite={canWrite} onSaved={handleConfigSaved} />

      {/* Reverb autobump — bump % + age gate. */}
      <AutobumpEditor row={configByChannel[BUMP_CHANNEL]} canWrite={canWrite} onSaved={handleConfigSaved} />

      {/* Curve overview — chart + month-by-month table */}
      <div className="card" style={{ padding: '12px 16px', marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>Time-decay curve</div>
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 12 }}>
          Effective list price per channel by stock age. Reference anchor A = {anchorPounds}; prices are the
          customer-facing rounded values the engine would list at.
        </div>
        <div style={{ display: 'flex', gap: 16, marginBottom: 8, flexWrap: 'wrap' }}>
          {CHANNELS.map(c => (
            <span key={c.key} style={{ fontSize: 11, color: '#6b7280', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: c.color }} />
              {c.label}{c.key !== 'shopify' ? ` (D2C ${pct(1 + curve.channel_uplift[c.key][0])})` : ''}
            </span>
          ))}
        </div>
        <div style={{ background: 'var(--color-bg-secondary, #f3eee6)', borderRadius: 6, padding: 10, marginBottom: 14 }}>
          <CurveChart series={series} months={curve.months} />
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="table" style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ width: 70 }}>Month</th>
                <th style={{ width: 90 }}>D2C × A</th>
                <th style={{ width: 110 }}><span style={{ color: CHANNELS[0].color }}>● </span>D2C</th>
                <th style={{ width: 110 }}><span style={{ color: CHANNELS[1].color }}>● </span>Reverb</th>
                <th style={{ width: 110 }}><span style={{ color: CHANNELS[2].color }}>● </span>eBay</th>
                <th>Reverb / eBay uplift on D2C</th>
              </tr>
            </thead>
            <tbody>
              {curve.months.map((m, i) => (
                <tr key={m}>
                  <td style={{ fontWeight: 600 }}>{m === curve.months[curve.months.length - 1] ? `M${m}+` : `M${m}`}</td>
                  <td>{(curve.d2c_multiplier[i] * 100).toFixed(curve.d2c_multiplier[i] * 100 % 1 ? 1 : 0)}%</td>
                  <td>{formatCurrency(curve.reference_prices.shopify[m] / 100)}</td>
                  <td>{formatCurrency(curve.reference_prices.reverb[m] / 100)}</td>
                  <td>{formatCurrency(curve.reference_prices.ebay[m] / 100)}</td>
                  <td style={{ color: '#6b7280' }}>
                    Reverb {pct(1 + curve.channel_uplift.reverb[i])} · eBay {pct(1 + curve.channel_uplift.ebay[i])}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Consignment: frozen terms + the descent curve, grouped directly below
          the main D2C time-decay curve (operator note 2026-09-21). */}
      <ConsignmentTermsEditor row={master} canWrite={canWrite} onSaved={handleConfigSaved} />
      <ConsignmentCurveCard row={master} curve={curve} canWrite={canWrite} onSaved={handleConfigSaved} />

      {/* Deferred roadmap (accurate as of the read-only phase) */}
      <div className="card" style={{ padding: '12px 16px', opacity: 0.85 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
          Not yet wired here <span style={{ fontSize: 10, background: 'rgba(34,197,94,0.18)', color: '#22c55e', padding: '1px 6px', borderRadius: 3, marginLeft: 6 }}>next</span>
        </div>
        <ul style={{ margin: '0 0 12px', paddingLeft: 18, fontSize: 12, color: '#6b7280', lineHeight: 1.7 }}>
          <li><strong>Editing the rollout</strong> — toggling enabled / dry-run / min-age from the UI (needs the writable config endpoint + security review).</li>
          <li><strong>Editing the curve</strong> — operator-tunable monthly multipliers / uplifts (needs a DB-backed curve store).</li>
          <li><strong>Going live + override UI</strong> — flipping a channel out of dry-run, per-product fixed-price pins.</li>
        </ul>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
          Phase 3 — curve enrichment <span style={{ fontSize: 10, background: 'rgba(107,114,128,0.2)', color: '#9ca3af', padding: '1px 6px', borderRadius: 3, marginLeft: 6 }}>later</span>
        </div>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#6b7280', lineHeight: 1.7 }}>
          <li><strong>D2C zig-zag</strong> — fortnightly oscillation up to an A + 5% ceiling above the descending floor.</li>
          <li><strong>Curve segmentation</strong> — different curves by category and price band.</li>
        </ul>
      </div>
    </div>
  )
}

export default PricingRules
